/**
 * Onboarding page for organization setup.
 *
 * Implements Task 012: Onboarding & Organization Flow.
 * Shows when user is authenticated but has no organization.
 *
 * Two paths:
 * 1. Create a new organization (become owner)
 * 2. Join an existing organization via invite code
 */

import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { organizationService } from '@/lib/organization.service'
import { useAuth } from '@/contexts/AuthContext'
import SirenLookup from '@/components/company/SirenLookup'
import CompanyDataForm, { emptyCompanyData } from '@/components/company/CompanyDataForm'
import type { CompanyData } from '@/lib/company-lookup.service'

type OnboardingStep = 'choose' | 'create' | 'join'

export default function OnboardingPage() {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: isAuthLoading, needsOnboarding, onOrgCreated } = useAuth()
  const [step, setStep] = useState<OnboardingStep>('choose')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Prevents the guard effect from racing with explicit navigation after org creation.
  // Without this, onOrgCreated() sets needsOnboarding=false → guard effect fires
  // navigate('/home') → strips ?guide=1 from the handler's navigate('/home?guide=1').
  const isNavigatingRef = useRef(false)

  // User name (shared between create and join flows)
  const [userName, setUserName] = useState('')

  // Create org form state
  const [companyName, setCompanyName] = useState('')
  const [isDemoOrg, setIsDemoOrg] = useState(false)
  const [companyData, setCompanyData] = useState<CompanyData>(emptyCompanyData())
  const [sirenFilled, setSirenFilled] = useState(false)
  const [editingDetails, setEditingDetails] = useState(false)
  const [acceptedResponsibility, setAcceptedResponsibility] = useState(false)

  // Join org form state
  const [inviteCode, setInviteCode] = useState('')

  // Redirect unauthenticated users to login
  useEffect(() => {
    if (!isAuthLoading && !isAuthenticated) {
      navigate('/login', { replace: true })
    }
  }, [isAuthLoading, isAuthenticated, navigate])

  // Redirect users who already have an org to home
  useEffect(() => {
    if (!isAuthLoading && isAuthenticated && !needsOnboarding && !isNavigatingRef.current) {
      navigate('/home', { replace: true })
    }
  }, [isAuthLoading, isAuthenticated, needsOnboarding, navigate])

  // Show nothing while checking auth
  if (isAuthLoading || !isAuthenticated) {
    return null
  }

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!userName.trim()) {
      setError('Please enter your name')
      return
    }
    if (!companyName.trim()) {
      setError('Please enter a company name')
      return
    }
    if (sirenFilled && !acceptedResponsibility) {
      setError('Please confirm you are authorized to act on behalf of this company')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      // Only include company_data if at least one meaningful field is filled
      const hasCompanyData = Object.entries(companyData).some(
        ([k, v]) => v != null && v !== '' && k !== 'address_country' && k !== 'capital_currency'
      )
      const org = await organizationService.createOrganization({
        name: companyName.trim(),
        is_demo: isDemoOrg,
        user_name: userName.trim(),
        ...(hasCompanyData ? { company_data: companyData } : {}),
      })
      // Directly update auth state with the returned org — no second API call
      // that could fail transiently and leave needsOnboarding=true.
      onOrgCreated(org)
      // Prevent the guard effect from navigating to /home (without ?guide=1)
      // before the explicit navigation below takes effect.
      isNavigatingRef.current = true
      // Navigate to home with ?guide=1 — GlobalLayout reads this from the URL
      // synchronously during mount and auto-opens the ASA onboarding sidebar.
      navigate('/home?guide=1', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create organization')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleJoinOrg = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!userName.trim()) {
      setError('Please enter your name')
      return
    }
    if (!inviteCode.trim()) {
      setError('Please enter an invitation code')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const org = await organizationService.joinOrganization({
        invite_code: inviteCode.trim(),
        user_name: userName.trim(),
      })
      // Directly update auth state with the returned org
      onOrgCreated(org)
      isNavigatingRef.current = true
      navigate('/home', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join organization')
    } finally {
      setIsSubmitting(false)
    }
  }

  const goBack = () => {
    setStep('choose')
    setError(null)
    setUserName('')
    setCompanyName('')
    setInviteCode('')
    setIsDemoOrg(false)
    setCompanyData(emptyCompanyData())
    setSirenFilled(false)
    setEditingDetails(false)
    setAcceptedResponsibility(false)
  }

  return (
    <div className={`w-full relative ${step === 'create' ? 'max-w-4xl' : 'max-w-[480px]'} transition-all duration-300`}>
      {/* Card Container */}
      <div className={`bg-background-panel/80 backdrop-blur-xl border border-white/20 dark:border-white/10 shadow-2xl rounded-[2.5rem] relative overflow-hidden ${step === 'create' ? 'p-6 md:p-8' : 'p-8 md:p-10'}`}>

        {/* Header — compact inline for create, centered for other steps */}
        {step === 'create' ? (
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 bg-amber-500/10 rounded-xl flex items-center justify-center text-2xl shrink-0 shadow-inner border border-amber-500/20">
              🐝
            </div>
            <div>
              <h1 className="text-lg font-display font-bold text-slate-900 dark:text-white">
                Create Your Organization
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Enter your company details
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center text-center mb-8">
            <div className="w-16 h-16 bg-amber-500/10 rounded-2xl flex items-center justify-center text-4xl mb-6 shadow-inner border border-amber-500/20">
              🐝
            </div>
            <h1 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-2">
              {step === 'choose' && 'Welcome to SmartBeez'}
              {step === 'join' && 'Join Organization'}
            </h1>
            <p className="text-sm text-slate-500 dark:text-white">
              {step === 'choose' && 'Set up your workspace to get started'}
              {step === 'join' && 'Enter your invitation code'}
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center gap-3">
            <div className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
            <p className="text-xs font-bold text-red-500">{error}</p>
          </div>
        )}

        {/* Choose Step */}
        {step === 'choose' && (
          <div className="space-y-4">
            <button
              onClick={() => setStep('create')}
              className="w-full p-5 bg-gradient-to-br from-amber-500/10 to-amber-600/5 hover:from-amber-500/20 hover:to-amber-600/10 border border-amber-500/20 rounded-2xl text-left transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center text-xl shrink-0 group-hover:scale-110 transition-transform">
                  🏢
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-1">
                    Create New Organization
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-white">
                    Start fresh with your own workspace. You'll become the owner.
                  </p>
                </div>
              </div>
            </button>

            <button
              onClick={() => setStep('join')}
              className="w-full p-5 bg-gradient-to-br from-violet-500/10 to-violet-600/5 hover:from-violet-500/20 hover:to-violet-600/10 border border-violet-500/20 rounded-2xl text-left transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-violet-500/20 rounded-xl flex items-center justify-center text-xl shrink-0 group-hover:scale-110 transition-transform">
                  🎟️
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-1">
                    Join with Invitation
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-white">
                    Have an invite code? Join an existing organization.
                  </p>
                </div>
              </div>
            </button>
          </div>
        )}

        {/* Create Organization Step */}
        {step === 'create' && (
          <form onSubmit={handleCreateOrg} className="space-y-3">
            {/* Required fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
                  Your Name
                </label>
                <input
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="John Doe"
                  className="w-full px-3 py-2 bg-white/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all"
                  autoFocus
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
                  Company Name
                </label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Acme Inc."
                  className="w-full px-3 py-2 bg-white/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* SIREN enrichment — hide search after filling */}
            {!sirenFilled ? (
              <div className="p-3 bg-amber-500/5 border border-amber-500/15 rounded-xl space-y-2">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-amber-500 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></svg>
                  <p className="text-sm font-semibold text-slate-700 dark:text-white">
                    Boost your AI with company data
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    — enter your SIREN or SIRET to auto-fill
                  </p>
                </div>
                <SirenLookup
                  disabled={isSubmitting}
                  accent="amber"
                  onCompanySelected={(data) => {
                    setCompanyData(data)
                    setSirenFilled(true)
                    if (data.legal_name) {
                      setCompanyName(data.legal_name)
                    }
                  }}
                />
              </div>
            ) : (
              <div className="space-y-3">
                {/* Compact summary + edit toggle */}
                <div className="rounded-xl border border-green-500/20 bg-green-500/5 p-3 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M20 6L9 17l-5-5" /></svg>
                      <span className="text-sm font-semibold text-slate-700 dark:text-white">Company data loaded</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => { setSirenFilled(false); setCompanyData(emptyCompanyData()); setAcceptedResponsibility(false) }}
                        className="text-xs font-medium text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                      >
                        Change company
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingDetails(!editingDetails)}
                        className="text-xs font-medium text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 transition-colors"
                      >
                        {editingDetails ? 'Hide details' : 'Review & edit'}
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
                    {companyData.legal_name && <p><span className="text-slate-700 dark:text-slate-300">{companyData.legal_name}</span></p>}
                    {(companyData.legal_form || companyData.legal_form_code) && <p>{companyData.legal_form || `Code ${companyData.legal_form_code}`}</p>}
                    {companyData.siren && <p>SIREN {companyData.siren}</p>}
                    {(companyData.naf_label || companyData.naf_code) && <p>{companyData.naf_label || `NAF ${companyData.naf_code}`}</p>}
                    {companyData.address_city && <p>{companyData.address_postal_code} {companyData.address_city}</p>}
                    {companyData.tva_number && <p>TVA {companyData.tva_number}</p>}
                  </div>
                </div>

                {/* Editable form — toggled, scrollable */}
                {editingDetails && (
                  <div className="overflow-y-auto rounded-xl border border-slate-200/80 dark:border-slate-600/50 p-3 bg-slate-50/50 dark:bg-slate-800/20" style={{ maxHeight: '35vh' }}>
                    <CompanyDataForm
                      value={companyData}
                      onChange={setCompanyData}
                      disabled={isSubmitting}
                      accent="amber"
                      layout="compact"
                    />
                  </div>
                )}

                {/* Legal responsibility engagement */}
                <div className="p-3 bg-amber-500/5 border border-amber-500/15 rounded-xl">
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={acceptedResponsibility}
                      onChange={(e) => setAcceptedResponsibility(e.target.checked)}
                      className="sr-only peer"
                      disabled={isSubmitting}
                    />
                    <div className="mt-0.5 w-4 h-4 shrink-0 rounded border border-slate-300 dark:border-slate-600 bg-white/50 dark:bg-slate-800/50 peer-checked:bg-amber-500 peer-checked:border-amber-500 peer-focus-visible:ring-2 peer-focus-visible:ring-amber-500/50 transition-all flex items-center justify-center peer-disabled:opacity-50">
                      {acceptedResponsibility && (
                        <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                      )}
                    </div>
                    <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                      I confirm that I am authorized to subscribe to SmartBeez on behalf of{' '}
                      <span className="font-semibold text-slate-800 dark:text-white">
                        {companyData.legal_name || companyName}
                      </span>
                      {companyData.siren && (
                        <span className="text-slate-400"> (SIREN {companyData.siren})</span>
                      )}
                      , and I accept responsibility for the use of this account.
                    </p>
                  </label>
                </div>
              </div>
            )}

            {/* Developer/Demo Org Option */}
            <div className="p-3 bg-slate-100/50 dark:bg-slate-800/30 rounded-xl">
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={isDemoOrg}
                  onChange={(e) => setIsDemoOrg(e.target.checked)}
                  className="sr-only peer"
                  disabled={isSubmitting}
                />
                <div className="mt-0.5 w-4 h-4 shrink-0 rounded border border-slate-300 dark:border-slate-600 bg-white/50 dark:bg-slate-800/50 peer-checked:bg-amber-500 peer-checked:border-amber-500 peer-focus-visible:ring-2 peer-focus-visible:ring-amber-500/50 transition-all flex items-center justify-center peer-disabled:opacity-50">
                  {isDemoOrg && (
                    <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                  )}
                </div>
                <div>
                  <span className="text-sm font-medium text-slate-700 dark:text-white">
                    I'm a developer
                  </span>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    Request a demo organization to explore features without payment.
                    Requires admin approval.
                  </p>
                </div>
              </label>
            </div>

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={goBack}
                disabled={isSubmitting}
                className="flex-1 py-2.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-white rounded-xl text-sm font-bold transition-all disabled:opacity-50"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !userName.trim() || !companyName.trim() || (sirenFilled && !acceptedResponsibility)}
                className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-sm font-bold shadow-lg shadow-amber-500/20 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  'Create Organization'
                )}
              </button>
            </div>
          </form>
        )}

        {/* Join Organization Step */}
        {step === 'join' && (
          <form onSubmit={handleJoinOrg} className="space-y-5">
            <div>
              <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                Your Name
              </label>
              <input
                type="text"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="John Doe"
                className="w-full px-4 py-3 bg-white/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500 transition-all"
                autoFocus
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                Invitation Code
              </label>
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                placeholder="Paste your invitation code"
                className="w-full px-4 py-3 bg-white/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-mono text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500 transition-all"
                disabled={isSubmitting}
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                Ask your team admin for an invitation code to join their organization.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={goBack}
                disabled={isSubmitting}
                className="flex-1 py-3 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-white rounded-xl text-sm font-bold transition-all disabled:opacity-50"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !userName.trim() || !inviteCode.trim()}
                className="flex-1 py-3 bg-violet-500 hover:bg-violet-600 text-white rounded-xl text-sm font-bold shadow-lg shadow-violet-500/20 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  'Join Organization'
                )}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Decorative blurred blobs */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full -z-10 blur-3xl opacity-20 pointer-events-none">
        <div className="absolute top-[-20%] left-[-20%] w-[80%] h-[80%] bg-amber-500 rounded-full mix-blend-screen animate-pulse" />
        <div className="absolute bottom-[-20%] right-[-20%] w-[80%] h-[80%] bg-violet-600 rounded-full mix-blend-screen animate-pulse delay-1000" />
      </div>
    </div>
  )
}
